import { Inject, Injectable, OnDestroy } from '@angular/core';
import { SanityProductControlOption } from '@gelato-api-ui/core/sanity/sanity-product-control-option';
import { FeatureWithKeyValue } from './config-cat/config-cat.provider';

import { SanityProductFilter } from '@gelato-api-ui/core/sanity/sanity-product-filter';
import { SanityProductResponse } from '@gelato-api-ui/core/sanity/sanity-product-response';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FEATURE_SWITCHER_SERVICE_TOKEN } from '../api/api.tokens';
import { PRODUCT_DYNAMIC_FEATURE_FLAG, PRODUCT_FILTERS_DYNAMIC_FEATURE_FLAG } from './constants/dynamic-feature-flags';
import { FeatureSwitcherService } from './feature-switcher.service';

@Injectable({
  providedIn: 'root',
})
export class SanityContentDynamicFeatureService implements OnDestroy {
  private featuresList: FeatureWithKeyValue[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly isPublic = this.featureSwitcherService.isPublic; // gelato.com

  constructor(@Inject(FEATURE_SWITCHER_SERVICE_TOKEN) private readonly featureSwitcherService: FeatureSwitcherService) {
    this.featureSwitcherService
      .getFeaturesList()
      .pipe(takeUntil(this.destroy$))
      .subscribe(features => (this.featuresList = [...features]));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Filter out product controls by dynamic feature flag for current user
   * @see https://gelato.atlassian.net/wiki/spaces/GAPI/pages/5876678657/3.15+Dynamic+ConfigCat+feature+flag+creation+guide
   */
  filterSanityProductControlsByDynamicFeatureFlag(
    productControls: SanityProductControlOption[],
    productId: string,
  ): SanityProductControlOption[] {
    return [
      ...productControls
        .map(option => {
          // Dynamic feature name
          const featureName = `${PRODUCT_DYNAMIC_FEATURE_FLAG}${productId}-${option.category}-${option.value}`.replace(
            /-/g,
            '_',
          );

          // Find the dynamic feature in the list of all features
          const foundFeature = this.featuresList.find(f => f.settingKey === featureName);

          if (foundFeature) {
            return foundFeature.settingValue && !this.isPublic ? option : null;
          } else {
            return option;
          }
        })
        // filter out values that are not for current user
        .filter(el => el),
    ];
  }

  /**
   * Filter out product filters by dynamic feature flag for current user
   * @see https://gelato.atlassian.net/wiki/spaces/GAPI/pages/5876678657/3.15+Dynamic+ConfigCat+feature+flag+creation+guide
   */
  removeFiltersUnderFeatureFlag(product: SanityProductResponse): SanityProductResponse {
    // filter the product filters based on the feature flag value
    const filters = product.filters.filter(filter => {
      if (this.isFilterUnderFlag(filter)) {
        return false;
      }

      return filter;
    });

    return { ...product, filters };
  }

  private isFilterUnderFlag(filter: SanityProductFilter): boolean {
    return this.featuresList.some(feature => {
      const isFilterUnderFlag =
        feature.settingKey === `${PRODUCT_FILTERS_DYNAMIC_FEATURE_FLAG}_${filter.key.toLocaleLowerCase()}`;

      const isFeatureDisabled = feature.settingValue;

      if (isFilterUnderFlag && (this.isPublic || !isFeatureDisabled)) {
        return true;
      }

      return false;
    });
  }
}
